export class LocalStorageUtils {
    private static readonly expireKey = '__LSU__expire__';
    private static readonly parseValueKey = '__LSU__value__';
    private static readonly valueType = '__LSU_value_type__';
    // noinspection JSUnusedLocalSymbols
    private constructor() {
        // no benefit in instantiating utility class
    }

    static setItem(
        key: string,
        value: string | Object,
        expireDate?: Date
    ): void {
        const actualValue =
            typeof value === 'string' ? value : JSON.stringify(value);
        localStorage.setItem(
            key,
            JSON.stringify({
                [this.parseValueKey]: actualValue,
                [this.expireKey]: expireDate?.toISOString(),
                [this.valueType]: typeof value,
            })
        );
    }

    static getItemOrNull<T extends object | string>(key: string): T | null {
        const value = localStorage.getItem(key);
        if (value === null) {
            return null;
        }
        try {
            const parsed = JSON.parse(value);
            if (!parsed[this.parseValueKey]) {
                return null;
            }
            if (parsed[this.valueType] === 'string') {
                return parsed[this.parseValueKey];
            } else {
                return JSON.parse(parsed[this.parseValueKey]);
            }
        } catch (e) {
            return null;
        }
    }

    static getItem<T extends object | string>(key: string): T | null {
        const value = localStorage.getItem(key);
        if (value === null) {
            return null;
        }
        try {
            const parsed = JSON.parse(value);
            if (!parsed[this.parseValueKey]) {
                // noinspection ExceptionCaughtLocallyJS
                throw new Error(
                    'Item was not saved by this util, try to use localstorage directly'
                );
            }
            if (parsed[this.valueType] === 'string') {
                return parsed[this.parseValueKey];
            } else {
                return JSON.parse(parsed[this.parseValueKey]);
            }
        } catch (e) {
            throw new Error(
                'Item was not saved by this util, try to use localstorage directly'
            );
        }
    }

    static addOrUpdate(
        key: string,
        value: string | Object,
        expireDate?: Date
    ): boolean {
        const prevValue = this.getItemOrNull(key);
        if (!prevValue) {
            this.setItem(key, value, expireDate);
            return false;
        } else {
            this.setItem(key, prevValue, expireDate);
            return true;
        }
    }

    static hasItem(key: string): boolean {
        const prevItem = this.getItemOrNull(key);
        return !!prevItem;
    }

    /**
     * Checks if item **That is added with this util** is expired.
     * In other words, return true if this item is added with this util AND the item is expired.
     * @param key
     */
    static isItemExpired(key: string): boolean {
        const value = localStorage.getItem(key);
        if (value === null) {
            return false;
        }
        try {
            const parsed = JSON.parse(value);
            if (!parsed[this.parseValueKey]) {
                return false;
            }
            if (!parsed[this.expireKey]) {
                return false;
            }
            return (
                new Date(parsed[this.expireKey]).getTime() <
                new Date().getTime()
            );
        } catch (e) {
            return false;
        }
    }

    static hasValidItem(key: string): boolean {
        const has = this.hasItem(key);
        const expired = this.isItemExpired(key);
        return has && !expired;
    }

    static removeItem(key: string): boolean {
        if (this.hasItem(key)) {
            localStorage.removeItem(key);
            return true;
        }
        return false;
    }

    static popItem<T extends object | string>(key: string): T | null {
        const prevItem = this.getItemOrNull<T>(key);
        if (prevItem) {
            this.removeItem(key);
        }
        return prevItem;
    }

    static revalidateStorage() {
        for (const key in { ...localStorage }) {
            if (this.isItemExpired(key)) {
                localStorage.removeItem(key);
            }
        }
    }
}
